import { Check, Clipboard } from "lucide-react";
import { type MouseEvent, useState } from "react";

import { getPatientLookupCopiableNameOverride } from "@/configuration/patient-lookup-copiable-name";
import { Field } from "@/src/components/cards/common";
import { COPIED_RESET_DELAY_MS } from "@/src/components/copyable-content";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/src/components/shadcn/ui/tooltip";
import type { ValerieCardDetail } from "@/src/components/valerie-card";
import { useUser } from "@/src/context/user-context";
import type { EhrIdentifiers } from "@/src/lib/resolve-ehr-ids";
import { cn } from "@/src/lib/shadcn-utils";
import { properCase } from "@/src/lib/string";
import { getDisplayValue } from "@/src/lib/utils/string-utils";
import { Patient } from "@/src/models/patient";
import {
  getPersonName,
  getPersonReverseName,
  type NameFields,
  PERSON_DEMOGRAPHIC_PLACEHOLDER_VALUES,
} from "@/src/models/person";

export const PATIENT_HEADER_FIELD_KEYS = new Set(["givenName", "familyName", "dateOfBirth", "gender", "ehrId", "mrn"]);

export type PatientIdentifierResolver = (patient: Patient) => EhrIdentifiers;

export function withResolvedEhrIdFields(fields: Field[], resolveIdentifiers: PatientIdentifierResolver): Field[] {
  return fields.flatMap((field) =>
    field.key === "ehrId"
      ? [
          {
            key: "ehrId",
            label: "EHR ID",
            comparable: false,
            section: field.section,
            getValue: (p: Patient) => resolveIdentifiers(p).ehrSystemId,
          },
          {
            key: "mrn",
            label: "MRN",
            comparable: false,
            section: field.section,
            getValue: (p: Patient) => resolveIdentifiers(p).mrn,
          },
        ]
      : [field]
  );
}

export function NamePart({
  value,
  label,
  display = properCase(value),
  className,
  onCopy,
}: {
  value: string;
  label: string;
  display?: string;
  className?: string;
  onCopy: (event: MouseEvent, text: string) => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn("cursor-pointer hover:underline", className)} onClick={(event) => onCopy(event, value)}>
          {display}
        </span>
      </TooltipTrigger>
      <TooltipContent>Copy {label}</TooltipContent>
    </Tooltip>
  );
}

export function PatientCardTitle({ patient }: { patient: NameFields }) {
  const { group } = useUser();
  const [copied, setCopied] = useState(false);
  const getFormattedName = (p: Pick<Patient, "givenName" | "familyName">) =>
    getPatientLookupCopiableNameOverride(group, p) ?? getPersonReverseName(p);
  const fullName = getFormattedName(patient) ?? getPersonName(patient).trim();
  const nameFormat = getFormattedName({ givenName: "First", familyName: "Last" });
  const { givenName, middleName, familyName } = patient;

  async function handleCopy(event: MouseEvent, text: string) {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), COPIED_RESET_DELAY_MS);
    } catch {}
  }

  if (!fullName) {
    return <span>Name not provided</span>;
  }

  return (
    <TooltipProvider>
      <span className="group text-foreground">
        {givenName && <NamePart value={givenName} label="first name" onCopy={handleCopy} />}
        {middleName && (
          <>
            {" "}
            <NamePart
              value={middleName}
              label="middle name"
              className="font-normal text-muted-foreground"
              onCopy={handleCopy}
            />
          </>
        )}{" "}
        {familyName && <NamePart value={familyName} label="last name" onCopy={handleCopy} />}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={`Copy name (${nameFormat})`}
              className="ml-1 align-baseline"
              onClick={(event) => handleCopy(event, fullName)}
            >
              {copied ? (
                <Check size={14} className="inline text-green-600" />
              ) : (
                <Clipboard
                  size={14}
                  className="relative top-[-1px] inline text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
                />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent>Copy name ({nameFormat})</TooltipContent>
        </Tooltip>
      </span>
    </TooltipProvider>
  );
}

/** Shows the MRN staff act on; the EHR system id only surfaces when the patient has no MRN. */
export function preferMrn(identifiers: EhrIdentifiers): EhrIdentifiers {
  return identifiers.mrn ? { mrn: identifiers.mrn, ehrSystemId: null } : identifiers;
}

export function getPatientCardDetails(patient: Patient, identifiers: EhrIdentifiers): ValerieCardDetail[] {
  return [
    { label: "DOB", value: patient.dateOfBirth ? getDisplayValue(patient.dateOfBirth, "date") : null },
    {
      label: "Gender",
      value: patient.gender && !PERSON_DEMOGRAPHIC_PLACEHOLDER_VALUES.has(patient.gender) ? patient.gender : null,
    },
    { label: "EHR ID", value: identifiers.ehrSystemId },
    { label: "MRN", value: identifiers.mrn },
  ];
}
